// ═══════════════════════════════════════════════════════════════════════════════
// BusinessVideoInterviewScreen.js — recruiter's video interview dashboard
//
// Integrated with:
//   • VideoScreen           (start the actual Jitsi call)
//   • ApplicantDetailsScreen (view full candidate profile)
//   • ScheduleInterviewScreen (create / reschedule)
//   • ContactCandidateScreen (send/email the meeting link)
//
// Pulls from /api/data/interviews JOINed with /api/data/applications
// Recordings tab pulls applications where interview_url IS NOT NULL.
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions,
  ScrollView, RefreshControl, StatusBar, Alert, Modal, ActivityIndicator,
  TextInput, Image, Share, Linking, Platform,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons, Feather } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── Theme ────────────────────────────────────────────────────────────────────
const { width } = Dimensions.get('window');
const T = {
  primary: '#0e4e96',
  primaryDark: '#0a3a72',
  bg:      '#F3F4F6',
  white:   '#FFFFFF',
  text:    '#0f172a',
  sub:     '#475569',
  mute:    '#9ca3af',
  border:  '#e2e8f0',
  success: '#10B981',
  warn:    '#F59E0B',
  danger:  '#EF4444',
  purple:  '#8B5CF6',
  pink:    '#EC4899',
};

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const apiFetch = async (endpoint, options = {}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token').catch(() => null);
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    if (!res.ok) return { error: true, status: res.status };
    return await res.json();
  } catch (err) {
    return { error: true, message: err.message };
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { return String(d); }
};
const fmtTime = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};
const fmtDuration = (s) => {
  if (!s) return '0m';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};
const initials = (name = '') =>
  String(name).split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const isToday    = (d) => d && new Date(d).toDateString() === new Date().toDateString();
const isFuture   = (d) => d && new Date(d).getTime() > Date.now();
const isPast     = (d) => d && new Date(d).getTime() < Date.now();
const isThisWeek = (d) => {
  if (!d) return false;
  const now = new Date(); const target = new Date(d);
  const start = new Date(now); start.setDate(start.getDate() - start.getDay());
  start.setHours(0, 0, 0, 0);
  const end = new Date(start); end.setDate(end.getDate() + 7);
  return target >= start && target < end;
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function BusinessVideoInterviewScreen({ navigation, route }) {
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab]   = useState('today');
  const [searchQuery, setSearchQuery] = useState('');
  const [companyId, setCompanyId]   = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [applications, setApplications] = useState([]);
  const [recordings, setRecordings] = useState([]);

  // ── Load company context ────────────────────────────────────────────────
  const loadCompanyId = async () => {
    try {
      const stored = await AsyncStorage.getItem('company_id');
      if (stored) return parseInt(stored);
      const profileRaw = await AsyncStorage.getItem('company_profile');
      if (profileRaw) {
        const p = JSON.parse(profileRaw);
        if (p.id) return parseInt(p.id);
      }
    } catch {}
    return route?.params?.company_id || null;
  };

  const loadCompanyProfile = async (cid) => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) setCompanyProfile(JSON.parse(cached));
    } catch {}
    if (!cid) return;
    const r = await apiFetch(`/api/data/company_profiles?id=${cid}&limit=1`);
    const data = Array.isArray(r) ? r[0] : (r?.data?.[0] || null);
    if (data) {
      setCompanyProfile(data);
      AsyncStorage.setItem('company_profile', JSON.stringify(data)).catch(() => {});
    }
  };

  // ── Load all interview data ────────────────────────────────────────────
  const loadAll = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const [interviewsRes, appsRes, recordingsRes] = await Promise.all([
        apiFetch(`/api/data/interviews?company_id=${cid}&sort=scheduled_date&order=desc&limit=200`),
        apiFetch(`/api/data/applications?company_id=${cid}&sort=created_at&order=desc&limit=500`),
        apiFetch(`/api/data/applications?company_id=${cid}&interview_url=__notnull&sort=interview_ended_at&order=desc&limit=100`),
      ]);

      const interviewsList = Array.isArray(interviewsRes) ? interviewsRes : (interviewsRes?.data || []);
      const appsList       = Array.isArray(appsRes)       ? appsRes       : (appsRes?.data       || []);
      const recordingsList = Array.isArray(recordingsRes) ? recordingsRes : (recordingsRes?.data || []);

      // Enrich interviews with their application's candidate info
      const appsById = new Map(appsList.map(a => [String(a.id), a]));
      const enriched = interviewsList.map(iv => {
        const app = appsById.get(String(iv.application_id)) || {};
        return {
          ...iv,
          _app:            app,
          candidate_name:  iv.candidate_name  || app.candidate_name  || 'Unknown',
          candidate_email: iv.candidate_email || app.candidate_email || '',
          candidate_phone: iv.candidate_phone || app.candidate_phone || '',
          job_title:       iv.job_title       || app.job_title       || app.position || '',
          application_id:  iv.application_id  || app.id              || null,
          when:            iv.scheduled_date  || iv.interview_date   || iv.created_at,
        };
      });

      // Recordings = applications with a real interview_url AND interview_ended_at
      const realRecordings = recordingsList
        .filter(a => a.interview_url && a.interview_ended_at)
        .map(a => ({
          ...a,
          when:           a.interview_ended_at,
          duration_s:     a.interview_duration_s || 0,
          candidate_name: a.candidate_name,
          job_title:      a.job_title || a.position || '',
        }));

      setInterviews(enriched);
      setApplications(appsList);
      setRecordings(realRecordings);
    } catch (err) {
      console.warn('loadAll error:', err);
    }
  }, []);

  // ── Init / refresh ─────────────────────────────────────────────────────
  const init = useCallback(async () => {
    setLoading(true);
    const cid = await loadCompanyId();
    setCompanyId(cid);
    await loadCompanyProfile(cid);
    await loadAll(cid);
    setLoading(false);
  }, [loadAll]);

  useFocusEffect(useCallback(() => { init(); }, [init]));

  const onRefresh = async () => {
    setRefreshing(true);
    await loadAll(companyId);
    setRefreshing(false);
  };

  // ── Filtering by tab + search ─────────────────────────────────────────
  const visible = useMemo(() => {
    let list = [];
    if (activeTab === 'today') {
      list = interviews.filter(i => isToday(i.when));
    } else if (activeTab === 'upcoming') {
      list = interviews.filter(i => isFuture(i.when) && !isToday(i.when));
    } else if (activeTab === 'completed') {
      list = interviews.filter(i =>
        i.status === 'completed' || (isPast(i.when) && i.status !== 'cancelled')
      );
    } else if (activeTab === 'recordings') {
      list = recordings;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(i =>
        (i.candidate_name || '').toLowerCase().includes(q) ||
        (i.candidate_email || '').toLowerCase().includes(q) ||
        (i.job_title || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeTab, interviews, recordings, searchQuery]);

  // ── Stats ───────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    today:     interviews.filter(i => isToday(i.when)).length,
    upcoming:  interviews.filter(i => isFuture(i.when)).length,
    week:      interviews.filter(i => isThisWeek(i.when)).length,
    completed: interviews.filter(i => i.status === 'completed' || isPast(i.when)).length,
    recordings: recordings.length,
  }), [interviews, recordings]);

  // ── Actions ─────────────────────────────────────────────────────────
  const startVideoCall = (interview) => {
    if (!interview.application_id && !interview.id) {
      Alert.alert('Cannot start', 'No application linked to this interview.');
      return;
    }
    navigation.navigate('VideoScreen', {
      interviewId:       interview.id,
      applicationId:     interview.application_id,
      applicantName:     interview.candidate_name,
      applicantEmail:    interview.candidate_email,
      applicantPhone:    interview.candidate_phone,
      position:          interview.job_title,
      jobTitle:          interview.job_title,
      department:        interview.department,
      interviewType:     interview.type || interview.interview_type || 'video',
      agenda:            interview.agenda || interview.notes,
      companyLogo:       companyProfile?.logo,
      companyName:       companyProfile?.company_name || companyProfile?.name,
      candidate_name:    interview.candidate_name,
      candidate_email:   interview.candidate_email,
    });
  };

  const viewApplicant = (interview) => {
    if (!interview.application_id) {
      Alert.alert('No applicant', 'No application linked to this interview.');
      return;
    }
    navigation.navigate('ApplicantDetailsScreen', {
      applicantId: interview.application_id,
      applicant:   interview._app || {},
    });
  };

  const reschedule = (interview) => {
    navigation.navigate('ScheduleInterviewScreen', {
      interviewId:    interview.id,
      applicationId:  interview.application_id,
      applicant:      interview._app || {},
      mode:           'reschedule',
    });
  };

  const messageCandidate = (interview) => {
    navigation.navigate('ContactCandidateScreen', {
      applicantId:   interview.application_id,
      applicant:     interview._app || {},
      candidateName: interview.candidate_name,
      candidateEmail: interview.candidate_email,
      prefilledLink: interview.meeting_url || interview.interview_url || '',
    });
  };

  const sendQuickReminder = async (interview) => {
    if (!interview.candidate_email) {
      Alert.alert('No email', 'No email address on file for this candidate.');
      return;
    }
    const link = interview.meeting_url || interview.interview_url ||
      `https://meet.jit.si/JobEng-${(interview.candidate_name||'candidate').toLowerCase().replace(/\W+/g,'-')}-${interview.id}`;
    const subject = encodeURIComponent(`Interview reminder${interview.job_title ? ` — ${interview.job_title}` : ''}`);
    const body = encodeURIComponent(
      `Hi ${(interview.candidate_name || 'there').split(' ')[0]},\n\n` +
      `This is a friendly reminder of your upcoming interview${interview.when ? ` on ${fmtDate(interview.when)} at ${fmtTime(interview.when)}` : ''}.\n\n` +
      `Join the video call: ${link}\n\n` +
      `${companyProfile?.company_name || companyProfile?.name || 'JobEng'}\n`
    );
    try {
      await Linking.openURL(`mailto:${interview.candidate_email}?subject=${subject}&body=${body}`);
    } catch {}
  };

  const newInterview = () => {
    navigation.navigate('ScheduleInterviewScreen', { mode: 'new' });
  };

  const viewRecording = (rec) => {
    if (!rec.interview_url) return;
    Linking.openURL(rec.interview_url).catch(() => {});
  };

  // ─── RENDER ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView style={st.loadingContainer}>
        <StatusBar barStyle="light-content" />
        <ActivityIndicator size="large" color={T.primary} />
        <Text style={st.loadingText}>Loading interviews…</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" />

      {/* HEADER */}
      <LinearGradient colors={[T.primary, T.primaryDark]} style={st.header}>
        <View style={st.headerRow}>
          <TouchableOpacity onPress={() => navigation?.goBack()} style={st.iconBtn}>
            <Ionicons name="arrow-back" size={22} color="#fff" />
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={st.headerTitle}>Video Interviews</Text>
            <Text style={st.headerSub}>
              {companyProfile?.company_name || companyProfile?.name || 'All scheduled calls'}
            </Text>
          </View>
          {companyProfile?.logo ? (
            <Image source={{ uri: companyProfile.logo }} style={st.headerLogo} />
          ) : null}
        </View>
      </LinearGradient>

      {/* STATS STRIP */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        style={st.statsStrip} contentContainerStyle={{ paddingHorizontal: 12, paddingVertical: 12 }}>
        <StatCard label="Today"      value={stats.today}      color={T.danger}  icon="calendar-today" />
        <StatCard label="This Week"  value={stats.week}       color={T.warn}    icon="calendar-week" />
        <StatCard label="Upcoming"   value={stats.upcoming}   color={T.primary} icon="calendar-clock" />
        <StatCard label="Completed"  value={stats.completed}  color={T.success} icon="check-circle-outline" />
        <StatCard label="Recordings" value={stats.recordings} color={T.purple}  icon="record-rec" />
      </ScrollView>

      {/* TABS */}
      <View style={st.tabsRow}>
        {[
          { k: 'today',      label: 'Today',      count: stats.today },
          { k: 'upcoming',   label: 'Upcoming',   count: stats.upcoming },
          { k: 'completed',  label: 'Completed',  count: stats.completed },
          { k: 'recordings', label: 'Recordings', count: stats.recordings },
        ].map(t => (
          <TouchableOpacity key={t.k}
            style={[st.tab, activeTab === t.k && st.tabActive]}
            onPress={() => setActiveTab(t.k)}>
            <Text style={[st.tabText, activeTab === t.k && st.tabTextActive]}>{t.label}</Text>
            {t.count > 0 && (
              <View style={[st.tabBadge, activeTab === t.k && { backgroundColor: T.primary }]}>
                <Text style={[st.tabBadgeText, activeTab === t.k && { color: '#fff' }]}>{t.count}</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>

      {/* SEARCH */}
      <View style={st.searchRow}>
        <Ionicons name="search-outline" size={18} color={T.mute} />
        <TextInput
          style={st.searchInput}
          placeholder="Search by candidate, email, role…"
          placeholderTextColor={T.mute}
          value={searchQuery}
          onChangeText={setSearchQuery}
          returnKeyType="search"
        />
        {searchQuery ? (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={18} color={T.mute} />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* LIST */}
      <ScrollView
        style={{ flex: 1 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[T.primary]} />}
        contentContainerStyle={{ paddingBottom: 100 }}
      >
        {visible.length === 0 ? (
          <EmptyState tab={activeTab} onSchedule={newInterview} />
        ) : (
          visible.map((item, i) => (
            activeTab === 'recordings'
              ? <RecordingCard key={item.id || i} item={item} onView={() => viewRecording(item)} onApplicant={() => viewApplicant({ application_id: item.id, _app: item })} />
              : <InterviewCard
                  key={item.id || i}
                  item={item}
                  onStart={() => startVideoCall(item)}
                  onApplicant={() => viewApplicant(item)}
                  onReschedule={() => reschedule(item)}
                  onMessage={() => messageCandidate(item)}
                  onRemind={() => sendQuickReminder(item)}
                  isToday={isToday(item.when)}
                  isPast={isPast(item.when)}
                />
          ))
        )}
      </ScrollView>

      {/* FAB */}
      {activeTab !== 'recordings' && (
        <TouchableOpacity style={st.fab} onPress={newInterview}>
          <Ionicons name="add" size={26} color="#fff" />
        </TouchableOpacity>
      )}
    </SafeAreaView>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SUBCOMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════
const StatCard = ({ label, value, color, icon }) => (
  <View style={[st.statCard, { borderLeftColor: color }]}>
    <View style={[st.statIcon, { backgroundColor: color + '20' }]}>
      <MaterialCommunityIcons name={icon} size={16} color={color} />
    </View>
    <View>
      <Text style={st.statValue}>{value}</Text>
      <Text style={st.statLabel}>{label}</Text>
    </View>
  </View>
);

const InterviewCard = React.memo(({ item, onStart, onApplicant, onReschedule, onMessage, onRemind, isToday, isPast }) => {
  const accent = isPast ? T.mute : (isToday ? T.danger : T.primary);
  return (
    <View style={[st.card, { borderLeftColor: accent }]}>
      <TouchableOpacity onPress={onApplicant} activeOpacity={0.85}>
        <View style={st.cardHead}>
          <View style={[st.avatar, { backgroundColor: accent }]}>
            <Text style={st.avatarText}>{initials(item.candidate_name)}</Text>
          </View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={st.cardName} numberOfLines={1}>{item.candidate_name}</Text>
            {item.job_title ? <Text style={st.cardRole} numberOfLines={1}>{item.job_title}</Text> : null}
            <View style={st.cardMetaRow}>
              {item.when ? (
                <View style={st.metaPill}>
                  <Ionicons name="time-outline" size={11} color={accent} />
                  <Text style={[st.metaPillText, { color: accent }]}>
                    {isToday ? `Today, ${fmtTime(item.when)}` : `${fmtDate(item.when)} · ${fmtTime(item.when)}`}
                  </Text>
                </View>
              ) : null}
              {item.type || item.interview_type ? (
                <View style={st.metaPill}>
                  <Ionicons name="videocam-outline" size={11} color={T.sub} />
                  <Text style={st.metaPillText}>{item.type || item.interview_type}</Text>
                </View>
              ) : null}
              {item.duration_minutes ? (
                <View style={st.metaPill}>
                  <Ionicons name="hourglass-outline" size={11} color={T.sub} />
                  <Text style={st.metaPillText}>{item.duration_minutes}m</Text>
                </View>
              ) : null}
            </View>
          </View>
          <Ionicons name="chevron-forward" size={20} color={T.mute} />
        </View>
      </TouchableOpacity>

      {/* Actions */}
      <View style={st.actionRow}>
        <TouchableOpacity style={[st.actBtn, { backgroundColor: T.success }]} onPress={onStart}>
          <Ionicons name="videocam" size={14} color="#fff" />
          <Text style={st.actBtnText}>Start call</Text>
        </TouchableOpacity>
        <TouchableOpacity style={st.iconActBtn} onPress={onMessage}>
          <Ionicons name="chatbubble-ellipses-outline" size={16} color={T.primary} />
        </TouchableOpacity>
        <TouchableOpacity style={st.iconActBtn} onPress={onRemind}>
          <Ionicons name="mail-outline" size={16} color={T.primary} />
        </TouchableOpacity>
        <TouchableOpacity style={st.iconActBtn} onPress={onReschedule}>
          <Ionicons name="calendar-outline" size={16} color={T.warn} />
        </TouchableOpacity>
      </View>
    </View>
  );
});

const RecordingCard = React.memo(({ item, onView, onApplicant }) => (
  <View style={[st.card, { borderLeftColor: T.purple }]}>
    <TouchableOpacity onPress={onApplicant} activeOpacity={0.85}>
      <View style={st.cardHead}>
        <View style={[st.avatar, { backgroundColor: T.purple }]}>
          <MaterialCommunityIcons name="record-rec" size={20} color="#fff" />
        </View>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={st.cardName} numberOfLines={1}>{item.candidate_name || 'Candidate'}</Text>
          {item.job_title ? <Text style={st.cardRole} numberOfLines={1}>{item.job_title}</Text> : null}
          <View style={st.cardMetaRow}>
            {item.when ? (
              <View style={st.metaPill}>
                <Ionicons name="calendar-outline" size={11} color={T.sub} />
                <Text style={st.metaPillText}>{fmtDate(item.when)}</Text>
              </View>
            ) : null}
            {item.duration_s ? (
              <View style={st.metaPill}>
                <Ionicons name="timer-outline" size={11} color={T.sub} />
                <Text style={st.metaPillText}>{fmtDuration(item.duration_s)}</Text>
              </View>
            ) : null}
            {item.interview_evaluation?.recommend ? (
              <View style={[st.metaPill, { backgroundColor: T.success + '20' }]}>
                <Ionicons name="checkmark-circle" size={11} color={T.success} />
                <Text style={[st.metaPillText, { color: T.success }]}>{item.interview_evaluation.recommend}</Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </TouchableOpacity>
    <View style={st.actionRow}>
      <TouchableOpacity style={[st.actBtn, { backgroundColor: T.purple }]} onPress={onView}>
        <Ionicons name="play" size={14} color="#fff" />
        <Text style={st.actBtnText}>Open recording</Text>
      </TouchableOpacity>
      <TouchableOpacity style={st.iconActBtn} onPress={onApplicant}>
        <Ionicons name="person-outline" size={16} color={T.primary} />
      </TouchableOpacity>
    </View>
  </View>
));

const EmptyState = ({ tab, onSchedule }) => {
  const config = {
    today:      { icon: 'calendar-today', title: 'No interviews today',     msg: 'Your day is clear. Schedule one or check upcoming.', cta: 'Schedule' },
    upcoming:   { icon: 'calendar-blank', title: 'No upcoming interviews',  msg: 'Schedule an interview to get started.',              cta: 'Schedule' },
    completed:  { icon: 'check-circle-outline', title: 'No completed interviews', msg: 'Past interviews will appear here.',           cta: null },
    recordings: { icon: 'record-rec',     title: 'No recordings yet',       msg: 'Recordings show up here after interviews end.',      cta: null },
  }[tab] || {};
  return (
    <View style={st.empty}>
      <MaterialCommunityIcons name={config.icon || 'magnify'} size={56} color={T.mute} />
      <Text style={st.emptyTitle}>{config.title || 'Nothing here'}</Text>
      <Text style={st.emptyMsg}>{config.msg || ''}</Text>
      {config.cta ? (
        <TouchableOpacity style={st.emptyBtn} onPress={onSchedule}>
          <Ionicons name="add" size={16} color="#fff" />
          <Text style={st.emptyBtnText}>{config.cta}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: T.bg },
  loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: T.bg },
  loadingText: { fontSize: 14, color: T.sub, marginTop: 12 },

  // Header
  header: { paddingTop: Platform.OS === 'ios' ? 6 : 12, paddingBottom: 14, paddingHorizontal: 14 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  iconBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.12)' },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '800' },
  headerSub:   { color: 'rgba(255,255,255,0.85)', fontSize: 12, marginTop: 2 },
  headerLogo:  { width: 40, height: 40, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.2)' },

  // Stats
  statsStrip: { flexGrow: 0, backgroundColor: T.white, borderBottomWidth: 1, borderBottomColor: T.border },
  statCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: T.white, paddingVertical: 8, paddingHorizontal: 12,
    marginRight: 10, borderRadius: 10, borderLeftWidth: 3, borderWidth: 1, borderColor: T.border, minWidth: 110 },
  statIcon:  { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', marginRight: 8 },
  statValue: { fontSize: 18, fontWeight: '800', color: T.text },
  statLabel: { fontSize: 11, color: T.sub, marginTop: 1 },

  // Tabs
  tabsRow: { flexDirection: 'row', backgroundColor: T.white, paddingHorizontal: 8, paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: T.border },
  tab: { flexDirection: 'row', alignItems: 'center', flex: 1, justifyContent: 'center', paddingVertical: 10, gap: 4 },
  tabActive: { borderBottomWidth: 2, borderBottomColor: T.primary },
  tabText: { fontSize: 12, fontWeight: '600', color: T.sub },
  tabTextActive: { color: T.primary, fontWeight: '700' },
  tabBadge: { backgroundColor: T.bg, paddingHorizontal: 6, borderRadius: 10, minWidth: 18, alignItems: 'center' },
  tabBadgeText: { fontSize: 10, fontWeight: '700', color: T.sub },

  // Search
  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: T.white, marginHorizontal: 12, marginTop: 10,
    paddingHorizontal: 12, paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: T.border, gap: 8 },
  searchInput: { flex: 1, fontSize: 13, color: T.text },

  // Cards
  card: { backgroundColor: T.white, marginHorizontal: 12, marginTop: 10, borderRadius: 12, padding: 14,
    borderWidth: 1, borderColor: T.border, borderLeftWidth: 4 },
  cardHead: { flexDirection: 'row', alignItems: 'center' },
  avatar:   { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  avatarText: { color: '#fff', fontWeight: '800', fontSize: 15 },
  cardName: { fontSize: 15, fontWeight: '700', color: T.text },
  cardRole: { fontSize: 12, color: T.sub, marginTop: 2 },
  cardMetaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  metaPill: { flexDirection: 'row', alignItems: 'center', backgroundColor: T.bg, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10, gap: 3 },
  metaPillText: { fontSize: 10, fontWeight: '600', color: T.sub, textTransform: 'capitalize' },

  actionRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: T.border },
  actBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    paddingVertical: 9, borderRadius: 8 },
  actBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },
  iconActBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: T.bg, borderWidth: 1, borderColor: T.border },

  // Empty state
  empty: { alignItems: 'center', padding: 40, marginTop: 30 },
  emptyTitle: { fontSize: 16, fontWeight: '700', color: T.text, marginTop: 14 },
  emptyMsg: { fontSize: 13, color: T.sub, marginTop: 6, textAlign: 'center' },
  emptyBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: T.primary,
    paddingHorizontal: 18, paddingVertical: 10, borderRadius: 10, marginTop: 16 },
  emptyBtnText: { color: '#fff', fontWeight: '700', fontSize: 13 },

  // FAB
  fab: { position: 'absolute', right: 18, bottom: 24, width: 56, height: 56, borderRadius: 28,
    backgroundColor: T.primary, alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 8, shadowOffset: { width: 0, height: 4 }, elevation: 6 },
});